import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, PMLevelCard } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { getContrastColor } from "product-test-utils";

import { TestAppState } from "../../../app";
import { theme } from "../../../theme";
import { getDropdownColorValue } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import { getProjectName } from "../../../utils/get-plural-of-project-name";
import { isFieldEnabled } from "../../field-enabled";

/** Adds the category (or, for audits, severity) card (#2222). */
export function addCategoryCard(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const state = app.getAppState();
  const language = state.language || "en";
  const { settings } = state;
  const itemName = getItemName(settings?.configuration?.itemName, language);
  const itemNamePlural = getPluralOfItemName(
    settings?.configuration?.itemName,
    language
  );

  const isAudit = settings?.configuration?.useCase === "audit";
  const categoryLabel =
    settings?.configuration?.fieldsDefinition?.find(
      (field) => field.fieldName === "category"
    )?.label ?? getLocalizedMessage(language, "category");
  const { title: categoryCardTitle, description: categoryCardDescription } =
    isAudit
      ? {
          title: getLocalizedMessage(language, "severity"),
          description: getLocalizedMessage(language, "showsSeverity"),
        }
      : getLocalizedCardCopy(language, "reportCategory", {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
          categoryLabel,
        });
  const categoryCard = view.addComponent({
    component: PMLevelCard,
    layoutProps: {
      componentId: "categoryCard",
      title: categoryCardTitle,
      description: categoryCardDescription,
      labels: [categoryLabel],
    },
  });

  categoryCard.mapLayoutProps(({ settings, language }) => {
    const currentLanguage = language || "en";
    const categoryLabel =
      settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label ?? getLocalizedMessage(currentLanguage, "category");
    const isAudit = settings?.configuration?.useCase === "audit";
    const { title, description } = isAudit
      ? {
          title: getLocalizedMessage(currentLanguage, "severity"),
          description: getLocalizedMessage(currentLanguage, "showsSeverity"),
        }
      : getLocalizedCardCopy(currentLanguage, "reportCategory", {
          itemName: getItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          projectName: getProjectName(
            settings?.configuration?.projectName,
            currentLanguage
          ),
          categoryLabel,
        });

    return {
      componentId: "categoryCard",
      title,
      description,
      labels: [categoryLabel],
    };
  });

  categoryCard.mapState(({ currentReport, settings, language }) => {
    const backgroundColor = getDropdownColorValue(
      settings?.configuration?.categoryDefinition,
      currentReport?.project?.category,
      "#E1F1E7"
    );
    const contrastTextColor = getContrastColor(
      backgroundColor,
      theme.palette.text.primary
    );
    const currentLanguage = language || "en";
    const categoryLabel =
      settings?.configuration?.fieldsDefinition?.find(
        (field) => field.fieldName === "category"
      )?.label ?? getLocalizedMessage(currentLanguage, "category");
    const isAudit = settings?.configuration?.useCase === "audit";
    const { title, description } = isAudit
      ? {
          title: getLocalizedMessage(currentLanguage, "severity"),
          description: getLocalizedMessage(
            currentLanguage,
            "severityDescription"
          ),
        }
      : getLocalizedCardCopy(currentLanguage, "reportCategory", {
          itemName: getItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            currentLanguage
          ),
          projectName: getProjectName(
            settings?.configuration?.projectName,
            currentLanguage
          ),
          categoryLabel,
        });

    return {
      title,
      description,
      categories: [
        {
          value: currentReport?.project?.category ?? "n/a",
          valueColor: backgroundColor,
          textColor: contrastTextColor,
        },
      ],
      showInfoIcon: false,
      showMenuIcon: false,
    };
  });

  categoryCard.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "category", "projectBasic")
  );
}
